// src/components/common/ErrorBoundary.jsx
//
// Catches render-time errors anywhere below it in the tree.
//
// Its main job is to be the safety net for lazyWithRetry: if a dynamic
// import fails a second time (lazyWithRetry already tried one automatic
// reload) or throws for a reason that isn't a chunk-load error, this stops
// the user from seeing a blank page or a raw stack trace and instead shows
// a small "Something went wrong" card with a manual refresh button.
//
// Also doubles as a general-purpose fallback for any other unexpected
// render error in the app.

import { Component } from "react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log for visibility in the browser console / any error-tracking tool
    // that's wired up. Doesn't attempt to recover automatically here -
    // lazyWithRetry already handles the one-shot auto-reload for chunk
    // errors, so if we've reached this boundary, a further automatic reload
    // would risk looping.
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div
            style={{
              minHeight: "60vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "2rem",
              gap: "1rem",
            }}
          >
            <h2 style={{ fontSize: "1.25rem", fontWeight: 600 }}>
              Something went wrong
            </h2>
            <p style={{ color: "#666", maxWidth: "28rem" }}>
              A new version of the site may be available. Refreshing the
              page usually fixes this.
            </p>
            <button
              onClick={this.handleRefresh}
              style={{
                padding: "0.6rem 1.4rem",
                borderRadius: "0.5rem",
                border: "none",
                background: "#111",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Refresh page
            </button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
